"use client";

import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useLiveQuery } from "@/core/hooks/useLiveQuery";
import { offlineDb } from "@/core/offline/db";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Loader2 } from "lucide-react";
import { generateRapportPdf } from "@/modules/corrosion/report/pdf-generator";
import { downloadBlob } from "@/core/utils/file-download";

/**
 * Page aperçu PDF : génère le PDF du rapport et l'affiche dans un iframe
 * avec la visionneuse native du navigateur (zoom, scroll, impression).
 */
export default function RapportPreviewPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const campagneIdParam = searchParams.get("campagneId") ?? undefined;

  const site = useLiveQuery(
    () => (siteId ? offlineDb.sites.get(siteId) : undefined),
    [siteId]
  );
  const rapport = useLiveQuery(
    () =>
      siteId
        ? offlineDb.rapports.where("siteId").equals(siteId).first()
        : undefined,
    [siteId]
  );

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Générer le PDF au montage de la page
  useEffect(() => {
    if (!siteId) return;

    let cancelled = false;
    let currentUrl: string | null = null;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const blob = await generateRapportPdf(siteId, campagneIdParam);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        currentUrl = url;
        setPdfBlob(blob);
        setPdfUrl(url);
      } catch (err) {
        if (cancelled) return;
        console.error("Erreur génération aperçu PDF:", err);
        setError(
          err instanceof Error ? err.message : "Erreur lors de la génération"
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [siteId, campagneIdParam]);

  const fileName = `Rapport_${site?.name ?? "site"}_${rapport?.numero ?? ""}.pdf`
    .replace(/[/\\?%*:|"<>]/g, "_");

  return (
    <div className="flex flex-col h-screen bg-muted">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 border-b bg-background shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/sites/${siteId}/rapport`)}
            title="Retour à l'édition"
          >
            <ArrowLeft className="size-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="font-semibold truncate">Aperçu du rapport</h1>
            <p className="text-xs text-muted-foreground truncate">
              {site?.name ?? "Chargement…"}
              {rapport?.numero ? ` — ${rapport.numero}` : ""}
            </p>
          </div>
        </div>
        <Button
          size="sm"
          disabled={!pdfBlob}
          onClick={() => {
            if (pdfBlob) downloadBlob(pdfBlob, fileName);
          }}
        >
          <Download className="size-4" />
          Télécharger
        </Button>
      </header>

      {/* Contenu : iframe ou loader ou erreur */}
      <div className="flex-1 overflow-hidden">
        {loading && (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
            <Loader2 className="size-8 animate-spin" />
            <p>Génération de l'aperçu PDF…</p>
          </div>
        )}
        {error && !loading && (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-destructive px-6 text-center">
            <p className="font-medium">Impossible d'afficher l'aperçu</p>
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/sites/${siteId}/rapport`)}
            >
              Retour à l'édition
            </Button>
          </div>
        )}
        {pdfUrl && !loading && !error && (
          <iframe
            src={pdfUrl}
            className="w-full h-full border-0"
            title="Aperçu PDF"
          />
        )}
      </div>
    </div>
  );
}
